import React, { useEffect, useRef, useState } from 'react';
import qs from 'qs';
import * as Yup from 'yup';
import {
  Button,
  FormFieldCheckbox,
  FormFieldRadio,
  DealerCard,
  FormFieldGroup,
  FormFieldError,
  getLanguageCode,
} from '@triumph/shared';
import Label from 'components/_utility/Label';
import { useDispatch, useSelector } from 'react-redux';
import {
  getBikeCondition,
  getBikeModel,
  getConsent,
  getFormData,
  getGoal,
  getSelectedDealer,
  getSelectedMake,
  getSelectedModel,
  lgpdSelector,
  getOfferBanner,
  getCalculations,
  getLoadedConfiguration,
} from 'src/redux/app/selectors';
import { setStep, setSubstep } from 'src/redux/app/actions';
import { Form, Formik } from 'formik';
import { getHostname } from 'helpers/request';
import { getFromDataLayer } from 'helpers/utilities';
import Loader from 'components/_utility/Loader/Loader';
import useLabel from 'src/hooks/useLabel';
import useDataLayer from 'src/hooks/useDataLayer';
import { STEPPER_HEIGHT } from 'src/config/constants';
import {
  Wrap,
  Title,
  Text,
  ContentToggle,
  ChevronDown,
  AdditionalContent,
  Description,
  EditButton,
  Rule,
  WhatNext,
  WhatNextTitle,
  WhatNextContent,
  Error,
} from './YourDealerSummary.styled';

const YourDealerSummarySchema = isLgpdActive =>
  Yup.object().shape({
    dataConsent: isLgpdActive ? Yup.bool().oneOf([true], 'requiredLabel') : undefined,
    agreeToTerms: isLgpdActive ? Yup.bool().oneOf([true], 'requiredLabel') : undefined,
    privacyConsent: isLgpdActive ? undefined : Yup.bool().oneOf([true], 'requiredLabel'),
  });

const YourDealerSummary = () => {
  const dispatch = useDispatch();
  const wrapRef = useRef();
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [showLgpdContent, setShowLgpdContent] = useState(false);
  const getLabel = useLabel();
  const pushToDataLayer = useDataLayer();
  const selectedDealer = useSelector(getSelectedDealer);
  const consent = useSelector(getConsent);
  const markerImg = `${window.location.origin}/_images/map/map-icon-triumph-a.png`;
  const bikeModel = useSelector(getBikeModel);
  const formData = useSelector(getFormData);
  const { savedConfigurationUrl } = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const selectedMake = useSelector(getSelectedMake);
  const selectedModel = useSelector(getSelectedModel);
  const bikeCondition = useSelector(getBikeCondition);
  const goalId = useSelector(getGoal);
  const lgpd = useSelector(lgpdSelector);
  const offerBanner = useSelector(getOfferBanner);
  const calculations = useSelector(getCalculations);
  const loadedConfiguration = useSelector(getLoadedConfiguration);

  const warrantyPackTitle = loadedConfiguration?.extras?.warrantyPacks[0]?.title;
  const warrantyPackPrice = loadedConfiguration?.extras?.warrantyPacks[0]?.price;
  const servicingPacksTitle = loadedConfiguration?.extras?.servicingPacks[0]?.title;
  const servicingPacksPrice = loadedConfiguration?.extras?.servicingPacks[0]?.price;

  useEffect(() => {
    const y = wrapRef.current.getBoundingClientRect().top - document.body.getBoundingClientRect().top - STEPPER_HEIGHT;
    window.scrollTo(0, y);
    setSubmissionError('');
  }, []);

  const handlePlaceOrder = async values => {
    setSubmitting(true);
    setSubmissionError('');

    const codeweaversQuote = calculations?.finance?.quote;

    const totalChargeForCredit = codeweaversQuote?.fees
      ? codeweaversQuote?.chargesForCredit + codeweaversQuote?.fees[0]?.amount
      : calculations?.amountOfCredit?.value;

    const factoryExtendedWarranty = warrantyPackTitle ? `${warrantyPackTitle} | ${warrantyPackPrice || 'POA'}` : '';
    const servicePack = servicingPacksTitle ? `${servicingPacksTitle} | ${servicingPacksPrice || ''}` : '';

    let postBody = {
      BikeId: bikeModel.id,
      BikeName: bikeModel.name,
      FinanceType: formData.financeOption,
      FirstName: formData.firstName,
      LastName: formData.lastName,
      Email: formData.email,
      PhoneNumber: formData.phone,
      AddressLine1: formData.addressLine1,
      AddressLine2: formData.addressLine2,
      City: formData.city,
      Postcode: formData.postcode,
      County: formData.county,
      Country: formData.country,
      IsBikeExchange: formData.partExchange === 'yes',
      ExchangeBikeMake: selectedMake?.name || '',
      ExchangeBikeModel: selectedModel?.name || '',
      ExchangeBikeRegistration: formData.registration,
      ExchangeBikeMileage: formData.mileage,
      ExchangeBikeCondition: bikeCondition?.value || '',
      DealerId: formData.dealerId,
      DataConsentOptIn: !!values.dataConsentOptIn,
      DataConsentDoNotContact: !!values.dataConsentDoNotContact,
      ReceiveCommunication: values.receiveCommunication,
      AgreeToTerms: values.agreeToTerms,
      PrivacyPolicy: consent.privacyAcceptanceLabel,
      Goal: goalId,
      CountryLanguage: getLanguageCode(),
      ConfigurationId: loadedConfiguration?.id || '',
      SavedConfigurationUrl: savedConfigurationUrl ? atob(savedConfigurationUrl) : '',
      OfferOne: offerBanner?.title || '',
      OfferTwo: offerBanner?.short_description || '',
      OfferThree: offerBanner?.long_description || '',
      FactoryExtendedWarranty: factoryExtendedWarranty,
      ServicePack: servicePack,
      UserLocation: formData.dealerSearchLocation,
      Medium: window.visitData.medium,
      Source: window.visitData.source,
      CampaignName: window.visitData.campaignName,
      CampaignCode: window.visitData.campaignCode,
      CaptureUrl: window.location.href,
      CaptureComponent: document.getElementById('purchase-intent-form')?.getAttribute?.('data-sc-component'),
    };

    if (codeweaversQuote || calculations) {
      postBody = {
        ...postBody,
        MonthlyPaymentsOf: codeweaversQuote?.regularPayment || calculations?.monthlyPayment?.value || 0,
        OptionalFinalRepayment: codeweaversQuote?.finalPayment || calculations?.lastPayment?.value || 0,
        OptionToPurchaseFee: codeweaversQuote?.completionFee || 0,
        TermOfAgreementMonths: codeweaversQuote?.contractLength || calculations?.duration?.value || 0,
        OnTheRoadPrice: codeweaversQuote?.totalPrice || calculations?.retailPrice?.value || 0,
        TotalChargeForCredit: totalChargeForCredit || 0,
        CustomerDeposit: codeweaversQuote?.deposit?.cash || calculations?.downPayment?.value || 0,
        BalanceToFinance: codeweaversQuote?.balance || calculations?.totalAmountFinanced?.value || 0,
        TotalAmountPayable: codeweaversQuote?.totalAmountPayable || 0,
        DepositContribution: codeweaversQuote?.totalDepositContribution || 0,
        RateOfInterest: codeweaversQuote?.rateOfInterest || calculations?.effectiveInterest?.value || 0,
        AnnualContractMileage: codeweaversQuote?.annualMileage || 0,
        TotalDeposit: codeweaversQuote?.totalDeposit || calculations?.downPayment?.value || 0,
        RepresentativeApr: codeweaversQuote?.apr || calculations?.nominalInterest?.value || 0,
        ExcessMileageCharge: codeweaversQuote?.excessMileageRate || 0,
      };
    }

    try {
      const res = await fetch(`${getHostname()}/api/v2/purchaseintent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: new URLSearchParams(postBody),
      });
      const resData = await res.json();

      if (!resData.Success) {
        throw new window.Error(resData.Body);
      }
      else
      {
        pushToDataLayer('purchaseConfirmed', {
          event: 'gtm.purchaseIntentSubmission',
          user_data: {
            sha_256_email_address: resData.EmailAddressHash,   
            sha_256_phone_number: resData.PhoneNumberHash
          },
        });
      }

      dispatch(setStep(3));
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error(ex);
      setSubmissionError(ex.message);
    }

    setSubmitting(false);
  };

  return (
    <>
      <Wrap ref={wrapRef}>
        <Formik
          initialValues={{
            dataConsent: false,
            dataConsentOptIn: '',
            dataConsentDoNotContact: '',
            receiveCommunication: false,
            agreeToTerms: false,
            privacyConsent: false,
          }}
          validationSchema={YourDealerSummarySchema(!!lgpd)}
          onSubmit={handlePlaceOrder}
        >
          {({ values, errors, touched, handleChange, handleBlur, setValues }) => (
            <Form>
              <Title>
                <Label name="selectedTitle"></Label>
              </Title>
              <Description>
                <Label name="selectedDescription"></Label>
              </Description>
              <EditButton
                onClick={() => {
                  dispatch(setSubstep('a'));
                  pushToDataLayer('editLink', {
                    sectionEdit: getLabel('dealerTitle'),
                  });
                }}
              >
                <Label name="dealerEditText"></Label>
              </EditButton>
              <DealerCard dealer={selectedDealer} markerImg={markerImg}></DealerCard>
              <Rule></Rule>
              {lgpd ? (
                <>
                  <Text>
                    <Label name="dataConsentLabel"></Label>
                  </Text>
                  <ContentToggle
                    role="button"
                    onClick={() => setShowLgpdContent(!showLgpdContent)}
                    isActive={showLgpdContent}
                  >
                    <Label name="moreContentToggleLabel"></Label>
                    <ChevronDown />
                  </ContentToggle>
                  <AdditionalContent isActive={showLgpdContent}>
                    <Label name="dataConsentAdditionalContentLabel"></Label>
                  </AdditionalContent>
                  <FormFieldGroup>
                    <FormFieldRadio
                      name="dataConsent"
                      id="dataConsentOptIn"
                      checked={values.dataConsentOptIn}
                      value="dataConsentOptIn"
                      onChange={() =>
                        setValues(prevValues => ({
                          ...prevValues,
                          dataConsent: true,
                          dataConsentOptIn: true,
                          dataConsentDoNotContact: false,
                        }))
                      }
                    >
                      <Label name="dataConsentOptInLabel"></Label>
                    </FormFieldRadio>
                    <FormFieldRadio
                      name="dataConsent"
                      id="dataConsentDoNotContact"
                      checked={values.dataConsentDoNotContact}
                      value="dataConsentDoNotContact"
                      onChange={() =>
                        setValues(prevValues => ({
                          ...prevValues,
                          dataConsent: true,
                          dataConsentOptIn: false,
                          dataConsentDoNotContact: true,
                        }))
                      }
                    >
                      <Label name="dataConsentDoNotContactLabel"></Label>
                    </FormFieldRadio>
                    {touched.dataConsent && errors.dataConsent && (
                      <FormFieldError>
                        <Label name="dataConsentRequired"></Label>
                      </FormFieldError>
                    )}
                  </FormFieldGroup>
                  {values.dataConsentOptIn && (
                    <FormFieldGroup>
                      <FormFieldCheckbox
                        name="receiveCommunication"
                        id="receiveCommunication"
                        checked={values.receiveCommunication}
                        onChange={handleChange}
                      >
                        <Label name="receiveCommunicationLabel"></Label>
                      </FormFieldCheckbox>
                    </FormFieldGroup>
                  )}
                  <FormFieldGroup>
                    <FormFieldCheckbox
                      name="agreeToTerms"
                      id="agreeToTerms"
                      checked={values.agreeToTerms}
                      onChange={handleChange}
                    >
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: getLabel(
                            'agreeToTermsLabel',
                            'I have read and understand the Triumph {0}, {1}, and {2}'
                          )
                            .replace(
                              '{0}',
                              `<a href="${lgpd.privacyPolicyLink}" target="_blank">${getLabel(
                                'privacyPolicyLinkLabel'
                              )}</a>`
                            )
                            .replace(
                              '{1}',
                              `<a href="${lgpd.cookiePolicyLink}" target="_blank">${getLabel(
                                'cookiePolicyLinkLabel'
                              )}</a>`
                            )
                            .replace(
                              '{2}',
                              `<a href="${lgpd.termsAndConditionsLink}" target="_blank">${getLabel(
                                'termsAndConditionsLinkLabel'
                              )}</a>`
                            ),
                        }}
                      />
                    </FormFieldCheckbox>
                    {touched.agreeToTerms && errors.agreeToTerms && (
                      <FormFieldError>
                        <Label name="agreeToTermsRequired"></Label>
                      </FormFieldError>
                    )}
                  </FormFieldGroup>
                </>
              ) : (
                <>
                  <FormFieldGroup>
                    <FormFieldCheckbox
                      name="receiveCommunication"
                      id="receiveCommunication"
                      html={consent.contactOptInLabel}
                      checked={values.receiveCommunication}
                      onChange={handleChange}
                    />
                  </FormFieldGroup>
                  <FormFieldGroup>
                    <FormFieldCheckbox
                      name="privacyConsent"
                      id="privacyConsent"
                      html={consent.privacyAcceptanceLabel}
                      checked={values.privacyConsent}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.privacyConsent && errors.privacyConsent && (
                      <FormFieldError>
                        <Label name="privacyAcceptanceRequired"></Label>
                      </FormFieldError>
                    )}
                  </FormFieldGroup>
                </>
              )}
              {submissionError && (
                <Error>
                  <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                      fill="white"
                    />
                  </svg>
                  {submissionError}
                </Error>
              )}
              <Button fluid primary type="submit" disabled={submitting}>
                <Label name="placeOrderButtonLabel"></Label>
              </Button>
              <WhatNext>
                <WhatNextTitle>
                  <Label name="whatNextTitle"></Label>
                </WhatNextTitle>
                <WhatNextContent>
                  <Label name="whatNextDescription"></Label>
                </WhatNextContent>
              </WhatNext>
            </Form>
          )}
        </Formik>
      </Wrap>
      {submitting && <Loader label="pleaseWaitLabel" />}
    </>
  );
};

export default YourDealerSummary;
